import cn from 'classnames';
import {
  ForwardRefRenderFunction,
  ReactNode,
  forwardRef,
  useCallback,
  useImperativeHandle,
} from 'react';
import { Accept, FileWithPath, useDropzone } from 'react-dropzone';

import fileIcon from './assets/file.svg';
import styles from './FileDropzone.module.scss';

export type FileDropzoneHandle = {
  open: () => void;
};

type FileDropzoneProps = {
  title: ReactNode;
  className?: string;
  titleClassName?: string;
  description?: ReactNode;
  descriptionClassName?: string;
  icon?: ReactNode;
  accept?: Accept;
  onFile: (file: File) => void;
};

// The file drop area used by upload modals — same look as the study upload
// dropzone (widgets/UploadStudyModalForm).
const InternalFileDropzone: ForwardRefRenderFunction<
  FileDropzoneHandle,
  FileDropzoneProps
> = (props, ref) => {
  const {
    title,
    className,
    titleClassName,
    description,
    descriptionClassName,
    icon,
    accept,
    onFile,
  } = props;

  const onDrop = useCallback(
    (acceptedFiles: FileWithPath[]) => {
      if (acceptedFiles[0]) {
        onFile(acceptedFiles[0]);
      }
    },
    [onFile],
  );

  const { getRootProps, getInputProps, isFocused, open } = useDropzone({
    onDrop,
    accept,
  });

  useImperativeHandle(ref, () => ({ open }), [open]);

  return (
    <div
      {...getRootProps()}
      className={cn(styles.container, isFocused && styles.isFocused, className)}
    >
      <input {...getInputProps()} />

      {icon ?? <img src={fileIcon} alt="" />}

      <h3 className={cn(styles.title, 'h6', titleClassName)}>{title}</h3>

      {description && (
        <p className={cn(styles.text, 'p3', descriptionClassName)}>
          {description}
        </p>
      )}
    </div>
  );
};

export const FileDropzone = forwardRef<FileDropzoneHandle, FileDropzoneProps>(
  InternalFileDropzone,
);
